// ===================================================================
// MAINSTREET — Analytics
// Chart.js-based analytics: sector breakdown, operational status,
// damage severity, resource offer types, status check response rates,
// facility infrastructure — ported from mainstreet-embedded.html
// renderAnalytics()
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Group, Stack, Loader, Center, Select,
} from '../../components/gab/GabComponents';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useMainStreetOpsStore } from '../../store/mainstreetOpsStore';
import { useAuthStore } from '../../store/authStore';
import { SECTOR_TYPES, OP_STATUS, OFFER_TYPES, SECTOR_COLORS, DAMAGE_TYPES } from './constants';
import { semantic } from '../../lib/colors';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, ChartTooltip, ChartLegend);

// ── Helpers ─────────────────────────────────────────────────────

function sectorLabel(id: string): string {
  const s = SECTOR_TYPES.find(st => st.id === id);
  return s ? s.label : (id || 'Other').replace(/_/g, ' ');
}

function offerLabel(id: string): string {
  const o = OFFER_TYPES.find(t => t.id === id);
  return o ? o.label : (id || 'Other').replace(/_/g, ' ');
}

const OP_STATUS_CHART_COLORS: Record<string, string> = {
  operational: '#07963F',
  limited: '#A67103',
  closed_temp: semantic.error,
  closed_damage: '#c0392b',
  destroyed: '#8B0000',
  unknown: '#939598',
};

const SEVERITY_CHART_COLORS: Record<string, string> = {
  critical: semantic.error,
  major: '#DF461B',
  moderate: '#A67103',
  minor: '#07963F',
};

export default function Analytics() {
  const { organizations, facilities, contacts, loadOrganizations, loadFacilities, loadContacts } = useMainStreetStore();
  const {
    statusChecks, statusResponses, damageReports, resourceOffers,
    loadStatusChecks, loadStatusResponses, loadDamageReports, loadResourceOffers,
  } = useMainStreetOpsStore();
  const { incidents, activeIncident } = useAuthStore();

  const [initialLoad, setInitialLoad] = useState(true);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(
    activeIncident ? String(activeIncident.id) : null,
  );

  const fetchData = useCallback(async () => {
    await Promise.all([
      loadOrganizations(), loadFacilities(), loadContacts(),
      loadStatusChecks(), loadStatusResponses(),
      loadDamageReports(), loadResourceOffers(),
    ]);
  }, [loadOrganizations, loadFacilities, loadContacts, loadStatusChecks, loadStatusResponses, loadDamageReports, loadResourceOffers]);

  useEffect(() => {
    fetchData().finally(() => setInitialLoad(false));
  }, [fetchData]);

  // Sync default when activeIncident changes
  useEffect(() => {
    if (activeIncident && !selectedIncidentId) {
      setSelectedIncidentId(String(activeIncident.id));
    }
  }, [activeIncident, selectedIncidentId]);

  const incidentOptions = useMemo(() =>
    incidents.map(i => ({ value: String(i.id), label: i.name })),
    [incidents],
  );

  // Incident scope — filter by selected incident (not just activeIncident)
  const incId = selectedIncidentId || null;
  const incDamage = useMemo(() => incId ? damageReports.filter(d => String(d.incidentId) === incId) : damageReports, [damageReports, incId]);
  const incOffers = useMemo(() => incId ? resourceOffers.filter(r => String(r.incidentId) === incId) : resourceOffers, [resourceOffers, incId]);
  const incChecks = useMemo(() => incId ? statusChecks.filter(c => String(c.incidentId) === incId) : statusChecks, [statusChecks, incId]);

  // ── Chart Data ────────────────────────────────────────────────

  // 1) Organizations by sector (bar)
  const sectorData = useMemo(() => {
    const counts: Record<string, number> = {};
    organizations.forEach(o => {
      const s = o.sectorType || 'other';
      counts[s] = (counts[s] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return {
      labels: sorted.map(([id]) => sectorLabel(id)),
      datasets: [{
        label: 'Organizations',
        data: sorted.map(([, count]) => count),
        backgroundColor: sorted.map(([id]) => SECTOR_COLORS[id] || '#939598'),
        borderRadius: 4,
      }],
    };
  }, [organizations]);

  // 2) Operational status (doughnut)
  const opStatusData = useMemo(() => {
    const items = OP_STATUS.map(os => ({
      label: os.label,
      count: organizations.filter(o => o.opStatus === os.id).length,
      color: OP_STATUS_CHART_COLORS[os.id] || '#939598',
    })).filter(i => i.count > 0);
    return {
      labels: items.map(i => i.label),
      datasets: [{
        data: items.map(i => i.count),
        backgroundColor: items.map(i => i.color),
        borderWidth: 2,
        borderColor: '#fff',
      }],
    };
  }, [organizations]);

  // 3) Damage by severity (bar)
  const severityData = useMemo(() => {
    const sev: Record<string, number> = { critical: 0, major: 0, moderate: 0, minor: 0 };
    incDamage.forEach(d => {
      const s = (d.severity || 'minor').toLowerCase();
      if (s in sev) sev[s]++;
    });
    const order = ['critical', 'major', 'moderate', 'minor'];
    return {
      labels: order.map(s => s.charAt(0).toUpperCase() + s.slice(1)),
      datasets: [{
        label: 'Damage Reports',
        data: order.map(s => sev[s]),
        backgroundColor: order.map(s => SEVERITY_CHART_COLORS[s] || '#939598'),
        borderRadius: 4,
      }],
    };
  }, [incDamage]);

  // 4) Damage types breakdown (bar)
  const damageTypesData = useMemo(() => {
    const counts: Record<string, number> = {};
    incDamage.forEach(d => {
      (d.damageTypes || '').split(',').forEach(t => {
        const trimmed = t.trim();
        if (trimmed) counts[trimmed] = (counts[trimmed] || 0) + 1;
      });
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    // Convert snake_case IDs to friendly labels
    const toLabel = (id: string) => DAMAGE_TYPES.find(d => d.id === id)?.label
      ?? id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return {
      labels: sorted.map(([label]) => toLabel(label)),
      datasets: [{
        label: 'Reports',
        data: sorted.map(([, count]) => count),
        backgroundColor: semantic.error,
        borderRadius: 4,
      }],
    };
  }, [incDamage]);

  // 5) Resource offers by type (doughnut)
  const offerTypeData = useMemo(() => {
    const counts: Record<string, number> = {};
    incOffers.forEach(o => {
      const t = o.offerType || 'other';
      counts[t] = (counts[t] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    // Data viz palette — hex values mirror --data-viz-* tokens
    const palette = ['#07963F', '#03AC9E', '#C68700', '#7589FF', '#D33423', '#A627FF', '#F4019F', '#0285C4', '#019184', '#F86A0B', '#939598'];
    return {
      labels: sorted.map(([id]) => offerLabel(id)),
      datasets: [{
        data: sorted.map(([, count]) => count),
        backgroundColor: sorted.map((_, i) => palette[i % palette.length]),
        borderWidth: 2,
        borderColor: '#fff',
      }],
    };
  }, [incOffers]);

  // 6) Status check response rates (bar)
  const responseRateData = useMemo(() => {
    const items = incChecks.map(c => {
      const responses = statusResponses.filter(r => String(r.statusCheckId) === String(c.id)).length;
      const target = c.targetType === 'all'
        ? organizations.length
        : c.targetOrgs ? c.targetOrgs.split(',').length : organizations.length;
      return {
        title: (c.title || '').substring(0, 25),
        responses,
        target: target || 1,
      };
    });
    return {
      labels: items.map(i => i.title),
      datasets: [
        {
          label: 'Responses',
          data: items.map(i => i.responses),
          backgroundColor: '#03AC9E', // data-viz-250
          borderRadius: 4,
        },
        {
          label: 'Target',
          data: items.map(i => i.target),
          backgroundColor: '#e2e8f0',
          borderRadius: 4,
        },
      ],
    };
  }, [incChecks, statusResponses, organizations]);

  // 7) Facility infrastructure stats
  const facilityStats = useMemo(() => {
    const withGen = facilities.filter(f => { const v = (f.hasGen || '').toLowerCase(); return v === 'yes' || v === 'true'; }).length;
    const withHazmat = facilities.filter(f => { const v = (f.hasHazmat || '').toLowerCase(); return v === 'yes' || v === 'true'; }).length;
    return { total: facilities.length, withGen, withHazmat };
  }, [facilities]);

  if (initialLoad) {
    return <Center h={400}><Loader color="#03AC9E" /></Center>;
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } } },
      x: { ticks: { font: { size: 11 } } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'right' as const, labels: { boxWidth: 12, font: { size: 11 } } } },
  };

  return (
    <div>
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">Analytics</Title>
          <Text size="sm" c="dimmed">
            {selectedIncidentId
              ? `${incidents.find(i => String(i.id) === selectedIncidentId)?.name || 'Incident'} -- Comprehensive overview`
              : 'All data -- Comprehensive overview'}
          </Text>
        </div>
        <Select
          placeholder="All incidents"
          data={incidentOptions}
          value={selectedIncidentId}
          onChange={setSelectedIncidentId}
          clearable
          style={{ minWidth: 220 }}
        />
      </Group>

      {/* Summary Stats */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} mb="md" spacing="sm">
        {[
          { label: 'Organizations', value: organizations.length, color: 'var(--data-viz-250)' },
          { label: 'Facilities', value: facilities.length, color: 'var(--data-viz-350)' },
          { label: 'Contacts', value: contacts.length, color: 'var(--data-viz-200)' },
          { label: 'Damage Reports', value: incDamage.length, color: semantic.error },
          { label: 'Resource Offers', value: incOffers.length, color: 'var(--data-viz-500)' },
          { label: 'Status Checks', value: incChecks.length, color: 'var(--color-primary-900)' },
        ].map(s => (
          <Paper key={s.label} p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${s.color}` }}>
            <Text size="xs" c="dimmed">{s.label}</Text>
            <Text fz={24} fw={700} c={s.color}>{s.value}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Row 1: Sector + Op Status */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Organizations by Sector</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            {sectorData.labels.length > 0
              ? <Bar data={sectorData} options={chartOptions} />
              : <Text size="sm" c="dimmed" ta="center" pt={60}>No organizations registered</Text>
            }
          </div>
        </Paper>

        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Operational Status Distribution</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            {opStatusData.labels.length > 0
              ? <Doughnut data={opStatusData} options={doughnutOptions} />
              : <Text size="sm" c="dimmed" ta="center" pt={60}>No status data</Text>
            }
          </div>
        </Paper>
      </SimpleGrid>

      {/* Row 2: Damage severity + Damage types */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Damage Reports by Severity</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            <Bar data={severityData} options={chartOptions} />
          </div>
        </Paper>

        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Damage Types Reported</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            {damageTypesData.labels.length > 0
              ? <Bar data={damageTypesData} options={chartOptions} />
              : <Text size="sm" c="dimmed" ta="center" pt={60}>No damage types reported</Text>
            }
          </div>
        </Paper>
      </SimpleGrid>

      {/* Row 3: Offer types + Response rates */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Resource Offers by Type</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            {offerTypeData.labels.length > 0
              ? <Doughnut data={offerTypeData} options={doughnutOptions} />
              : <Text size="sm" c="dimmed" ta="center" pt={60}>No resource offers</Text>
            }
          </div>
        </Paper>

        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Status Check Response Rates</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            {responseRateData.labels.length > 0
              ? <Bar data={responseRateData} options={{ ...chartOptions, plugins: { legend: { display: true, position: 'top' } } }} />
              : <Text size="sm" c="dimmed" ta="center" pt={60}>No status checks</Text>
            }
          </div>
        </Paper>
      </SimpleGrid>

      {/* Facility Infrastructure */}
      <Paper p="md" radius="sm" withBorder>
        <Text fw={600} size="sm" mb="sm">Facility Infrastructure</Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Stack gap={4} align="center">
            <Text fz={28} fw={700} c="#0891b2">{facilityStats.total}</Text>
            <Text size="xs" c="dimmed">Total Facilities</Text>
          </Stack>
          <Stack gap={4} align="center">
            <Text fz={28} fw={700} c="#07963F">{facilityStats.withGen}</Text>
            <Text size="xs" c="dimmed">With Generator</Text>
          </Stack>
          <Stack gap={4} align="center">
            <Text fz={28} fw={700} c={semantic.error}>{facilityStats.withHazmat}</Text>
            <Text size="xs" c="dimmed">With Hazmat</Text>
          </Stack>
        </SimpleGrid>
      </Paper>
    </div>
  );
}
